/**
 * What the product screen offers under "You might also like".
 *
 * Pure on purpose. What a customer is offered next to the thing they are
 * already looking at is a merchandising decision with money attached, so the
 * rule is testable without a DOM and lives in one place rather than inline in
 * the component. Same reasoning as the cart's crossSellRecommendations.
 *
 * ⚠️ CATEGORY, NOT "MOST POPULAR", AND THE DISTINCTION IS NOT COSMETIC. We hold
 * no popularity signal: nothing on this page or behind it counts how often a
 * product is bought, and the cart's "Popular with parents" row is a hand-curated
 * order rather than a measured one. A row labelled "most popular" would
 * therefore be asserting something we have not measured. Category is data the
 * server already resolves for every add-on (Addon#gift_category), so it is both
 * true and free.
 *
 * The fallback is what makes it usable. A category can hold a single product --
 * for a senior, Magnets is exactly one -- so category alone would leave that
 * product's own screen with an empty row. Same-category first, then the rest of
 * the catalogue in the order it arrived, which is the pricelist's own `position`
 * merchandising (the same order the shop's "Featured" sort keeps).
 */

// Matches the four columns the row renders across on desktop, so a full shelf
// fills the row rather than leaving columns empty.
export const MAX_RELATED = 4;

/**
 * @param addons     every add-on this customer may see, already audience-filtered
 *                   by giftAddonVisible and in pricelist order
 * @param current    the add-on whose screen this is
 * @param limit      how many to offer
 */
export default function relatedProducts(addons = [], current = null, limit = MAX_RELATED) {
  if (!current) return [];

  // Identity is by css_name, not object identity or id: the product screen is
  // reached BY css_name, and a variant family (senior vs elementary wallets)
  // shares one. Recommending the row the customer is already on -- or its
  // other-audience twin, which they cannot buy -- is the thing to avoid.
  const others = addons.filter((addon) => addon.cssName !== current.cssName);

  const sameCategory = others.filter((addon) => addon.giftCategory === current.giftCategory);
  const rest = others.filter((addon) => addon.giftCategory !== current.giftCategory);

  return [...sameCategory, ...rest].slice(0, limit);
}
